// Trip history — the log of drives actually completed (#68).
//
// Different concept from `savedTrips.ts`, which stores user-saved
// templates ("Morning commute", "Weekend loop") that the user manually
// saves and re-runs. tripHistory records what the user ACTUALLY drove,
// captured at arrival, and powers the Trip Journal view + the share
// flow per past drive.
//
// Storage: localStorage, capped at 200 entries (oldest evicted). Future
// work could sync to Supabase per-user, but local-only is fine for the
// beta — testers' devices each get their own history.

import type { LngLat } from "@/utils/polyline";
import type { TravelMode } from "@/api/routing";

const KEY = "pleasant.tripHistory.v1";
const MAX_ENTRIES = 200;

/** One past drive, captured at arrival. The shape mirrors what
 *  TripRecap consumed so the journal can render the same recap
 *  again from history. */
export interface TripHistoryEntry {
  /** Stable id — ulid-ish for ordering. */
  id: string;
  /** Wall-clock time of arrival, ms since epoch. */
  arrivedAt: number;
  destinationName: string;
  /** Optional start label (when user typed one, not GPS). */
  startName?: string;
  /** Trip endpoints. */
  startCoords?: LngLat;
  endCoords: LngLat;
  /** Travel mode at the time. */
  mode: TravelMode;
  /** Distance + duration as the user actually experienced them. */
  distanceM: number;
  /** Wall-clock duration (NavigationView's elapsed time), not engine duration. */
  durationS: number;
  /** Pleasant score the route ended at (post live-traffic adjustments). */
  pleasantScore: number;
  /** Mid-drive ratings — same shape as TripRecap's `ratings`. */
  ratings: Array<{
    pleasant: 0 | 1 | 2 | 3;
    label: string;
    streetName?: string;
    timestamp: number;
  }>;
  /** Top highlight chips ("Along Tarrytown Lakes", "Through Rockefeller State Park"). */
  highlights: string[];
  /** vs-Google line if we had one ("Same time as Google would have given you."). */
  vsGoogleLine?: string;
  /** Polyline preview — optional, only stored when small enough so the
   *  journal can render a thumbnail map without re-fetching routes. */
  coordinates?: LngLat[];
  /** Which route type was driven — Pleasant / Scenic / Fast badge on the
   *  trip card (Morgan: trips "should say... if they were pleasant,
   *  scenic or fast"). */
  routeClass?: "pleasant" | "scenic" | "fastest";
  /** Hearted by the user — favorites sort to the top of the journal. */
  favorite?: boolean;
  /** Personal 1–5 star rank ("like them and rank them"). */
  userRank?: number;
}

/** Patch a stored trip (favorite / userRank). No-op if the id is gone. */
export function updateTrip(
  id: string,
  patch: Partial<Pick<TripHistoryEntry, "favorite" | "userRank">>,
): TripHistoryEntry[] {
  const all = read();
  const i = all.findIndex((t) => t.id === id);
  if (i !== -1) {
    all[i] = { ...all[i], ...patch };
    write(all);
  }
  return all;
}

function read(): TripHistoryEntry[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is TripHistoryEntry =>
        e &&
        typeof e.id === "string" &&
        typeof e.arrivedAt === "number" &&
        typeof e.destinationName === "string" &&
        Array.isArray(e.endCoords),
    );
  } catch {
    return [];
  }
}

function write(entries: TripHistoryEntry[]): void {
  try {
    // Cap at MAX_ENTRIES — drop the oldest. Beta-stage testers won't
    // come close to 200 drives but we cap so localStorage doesn't
    // grow unbounded over time.
    const trimmed = entries.length > MAX_ENTRIES
      ? entries.slice(0, MAX_ENTRIES)
      : entries;
    localStorage.setItem(KEY, JSON.stringify(trimmed));
  } catch {
    /* localStorage quota — silent */
  }
}

/** Append a new entry, returning the new list (newest first). */
export function recordTrip(entry: Omit<TripHistoryEntry, "id" | "arrivedAt"> & {
  id?: string;
  arrivedAt?: number;
}): TripHistoryEntry {
  const full: TripHistoryEntry = {
    id: entry.id ?? cryptoRandomId(),
    arrivedAt: entry.arrivedAt ?? Date.now(),
    destinationName: entry.destinationName,
    startName: entry.startName,
    startCoords: entry.startCoords,
    endCoords: entry.endCoords,
    mode: entry.mode,
    distanceM: entry.distanceM,
    durationS: entry.durationS,
    pleasantScore: entry.pleasantScore,
    ratings: entry.ratings,
    highlights: entry.highlights,
    vsGoogleLine: entry.vsGoogleLine,
    coordinates: entry.coordinates,
    routeClass: entry.routeClass,
    favorite: entry.favorite,
    userRank: entry.userRank,
  };
  const existing = read();
  // Cheap dedupe: if the previous entry was to the same destination
  // within 60 seconds, replace it instead of appending (handles a
  // double-fire of onTripEnd, which shouldn't happen but cheap to
  // guard against).
  if (existing.length > 0) {
    const last = existing[0];
    const sameDestination =
      last.endCoords[0] === full.endCoords[0] &&
      last.endCoords[1] === full.endCoords[1];
    const within = Math.abs(full.arrivedAt - last.arrivedAt) < 60_000;
    if (sameDestination && within) {
      existing[0] = full;
      write(existing);
      return full;
    }
  }
  const next = [full, ...existing];
  write(next);
  return full;
}

/** List all trips, newest first. */
export function listTrips(): TripHistoryEntry[] {
  return read();
}

/** Look up one trip by id. */
export function getTrip(id: string): TripHistoryEntry | undefined {
  return read().find((e) => e.id === id);
}

/** Remove a trip. */
export function deleteTrip(id: string): void {
  write(read().filter((e) => e.id !== id));
}

/** Wipe history — useful for tester debugging. */
export function clearTrips(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* silent */
  }
}

function cryptoRandomId(): string {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through */
  }
  return `t_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}
